/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Feb 28 2024
 **/

import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";

import { useProperties } from "@/frameworks/libraries/properties";
import { Button, Row, useToast } from "@/frameworks/designStuff/components";
import defaultChatImageSrc from "@/assets/images/img_failed_loading_space.png";
import { useClipboard } from "@/frameworks/libraries/hooks";
import { _T } from "@rfice/language";
import { useConfirm, useFileManager, usePopover } from "@/frameworks/core/hooks";
import { POPOVER_ID, ZIndex } from "@/frameworks/core/commons";

import ChatSubMenu from "../../../ChatSubMenu";

type ImageSize = {
	width: number;
	height: number;
};

const DEFAUT_IMAGE_SIZE: ImageSize = {
	width: 200,
	height: 168
};

const MAX_WIDTH = {
	single: 360,
	multiple: 174
};

const MIN_WIDTH = {
	single: 50
};
interface ChatImageProps extends ChatServices.Format2.Elements.Image {
	onClick?: (image: ChatServices.Format2.Elements.Image) => void;
	single?: boolean;
	isMe?: boolean;
	isActivity?: boolean;
}

export default function ChatImage(props: ChatImageProps) {
	const [imageSize, setImageSize] = useState<ImageSize | null>(null);
	const ref = useRef<HTMLImageElement>(null);
	const properties = useProperties();
	const { toggle, hide } = usePopover();
	const { copy } = useClipboard(ref);
	const toast = useToast();
	const { confirm } = useConfirm();
	const { deleteFile } = useFileManager();
	const [isLoaded, setIsLoaded] = useState(false);

	useEffect(() => {
		if (isLoaded === false) {
			setImageSize({ width: DEFAUT_IMAGE_SIZE.width, height: DEFAUT_IMAGE_SIZE.height });
		}
	}, [isLoaded]);

	const ToggleMenu = () => {
		return (
			<ChatSubMenu
				onClickItem={(ev: React.MouseEvent<HTMLLIElement>) => {
					const target = ev.currentTarget as HTMLLIElement;
					const type = target.id as "update" | "reply" | "notice" | "delete" | "copy";
					switch (type) {
						case "copy":
							copy({
								callback: () => {
									hide(POPOVER_ID.CHAT_SUB_MENU);
									const type = _T("이미지");
									toast.show({
										type: "system",
										message: _T("{{type}}가 복사되었습니다.", { type })
									});
								}
							});
							break;
						case "delete":
							confirm(_T("정말 삭제하시겠습니까?"), {
								async onConfirm() {
									await deleteFile({ id: props.id });
								}
							});
							break;
						// TODO 삭제 관련된 로직 추가 예정
						default:
							break;
					}
					hide(POPOVER_ID.CHAT_IMAGE_SUB_MENU);
				}}
				popoverID={POPOVER_ID.CHAT_IMAGE_SUB_MENU}
				variant="miniPopup"
				isMe={props?.isMe ?? false}
				type="image"
			/>
		);
	};

	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();

		const target = e.currentTarget as HTMLButtonElement;

		toggle({
			popoverID: POPOVER_ID.CHAT_IMAGE_SUB_MENU,
			content: <ToggleMenu />,
			direction: {
				x: "left",
				y: "bottom"
			},
			anchorEl: target
		});
	};

	const onCLickMountRight = (ev: React.MouseEvent<HTMLElement>) => {
		ev.stopPropagation();

		const target = ev.currentTarget as HTMLButtonElement;
		const { left, width } = target.getBoundingClientRect();
		const { clientX } = ev;
		const relativeX = clientX - left;
		let positionX = 0;
		if (positionX + 200 > width) {
			positionX = width - 200;
		} else {
			positionX = relativeX;
		}

		if (ev.type === "contextmenu" && ev.nativeEvent.button) {
			toggle({
				popoverID: POPOVER_ID.CHAT_SUB_MENU,
				content: <ToggleMenu />,
				position: {
					x: ev.nativeEvent.offsetX,
					y: ev.nativeEvent.offsetY
				},
				anchorEl: target,
				hasMouseClickAction: true,
				zIndex: ZIndex.SIDEBAR
			});
			ev.preventDefault();
		}
	};

	const handleImageLoad = (ev: React.SyntheticEvent<HTMLImageElement>) => {
		setIsLoaded(true);
		setImageSize({
			width: ev.currentTarget.clientWidth,
			height: ev.currentTarget.clientHeight
		});
	};

	return (
		<StyledImage
			single={Boolean(props.single)}
			size={imageSize ?? { width: 0, height: 0 }}
			onClick={(e: React.MouseEvent) => {
				e.stopPropagation();
				return props.onClick?.(props);
			}}
			onContextMenu={onCLickMountRight}
			isActivity={props.isActivity || false}
		>
			{
				/**
				 * 이미지의 원본 height가 StyledImage 내의 min-height 값보다 클 경우
				 * 채팅에서 스크롤이 밀리는 레이아웃 시프트 현상이 발생
				 * 이미지 로딩이 되기 전 큰 임시 DOM을 생성하여 레이아웃 시프트 현상을 수정했음
				 */
				imageSize === null && <div css={{ minHeight: document.body.clientHeight, minWidth: "100%" }} />
			}
			<div
				style={{
					width: isLoaded ? "100%" : DEFAUT_IMAGE_SIZE.width,
					height: isLoaded ? "100%" : DEFAUT_IMAGE_SIZE.height,
					background:
						isLoaded || props.isActivity ? "none" : `url('${defaultChatImageSrc}') no-repeat center center`
				}}
			>
				<img
					style={{
						display: isLoaded ? "block" : "none",
						...(props.isActivity
							? {
									width: "100%",
									height: "100%",
									objectFit: "cover"
								}
							: {})
					}}
					ref={ref}
					title={props.title}
					alt={props.alt}
					src={
						props.url.startsWith("http")
							? // @description
								// props.url.split("?")[0] - 해당 코드 추가한 이유는 기존 모바일 측에서 쿼리스트링을 넣어 보내주고있어 FE측에서 보이지 않는 오류가 발생, 이를 막기위한 방어코드
								`${props.url.split("?")[0]}?h=${props.single ? MAX_WIDTH.single : MAX_WIDTH.multiple}`
							: `${properties.default.webDomain}${props.url}?h=${props.single ? MAX_WIDTH.single : MAX_WIDTH.multiple}`
					}
					onLoad={handleImageLoad}
					onError={() => {
						setImageSize({
							width: props.single ? MAX_WIDTH.single : MAX_WIDTH.multiple,
							height: props.single ? MAX_WIDTH.single : MAX_WIDTH.multiple
						});
					}}
				/>
			</div>

			<HoverIconArea className="image-icon" verticalAlign="center" gap={6}>
				<Button.Icon name="More24" size="small_18" filled="transparent" onClick={handleClick} />
			</HoverIconArea>
		</StyledImage>
	);
}

/**
 * **공통 규칙**
 * 1. `이미지는 원본 height를 기준으로 표시한다.`
 * 2. 원본 이미지의 `width가 height의 2배 이상`인 경우 `max-width`로 표시한다.
 * 3. 원본 이미지의 `height가 width의 2배 이상`인 경우 `min-width`로 표시한다.
 *
 * **single image 규칙**
 * 1. `max-height`: `360px`
 * 2. `max-width`: `부모의 width`
 * 3. `가로 세로가 모두 50px보다 작을 경우 50px 캔버스로 감싼다`
 *
 * **multi image 규칙**
 * 1. `max-height`: `174px`
 * 2. `max-width`: `max-height * 2`
 * 3. 원본 이미지의 `height가 max-height보다 작으면 세로 여백을 캔버스로 표시`한다.
 * 4. 3의 조건을 충족하면서 원본 이미지의 `width가 max-height보다 작으면 가로 여백을 캔버스로 표시`한다.
 */
const StyledImage = styled.div<{ single: boolean; size: ImageSize; isActivity: boolean }>`
	position: relative;
	display: inline-flex;
	justify-content: center;
	align-items: center;
	border-radius: 4px;
	margin: 0 4px;
	background: ${({ theme }) => theme.colors.accent_00_100};
	overflow: hidden;

	width: ${({ isActivity }) => {
		return isActivity ? "100px" : "auto";
	}};

	height: ${({ isActivity }) => {
		return isActivity ? "100px" : "auto";
	}};

	min-height: ${({ single, isActivity }) => {
		if (isActivity) return "auto";

		if (single) return `${MIN_WIDTH.single}px`;

		return `${MAX_WIDTH.multiple}px`;
	}};
	max-height: ${({ single }) => {
		if (single) return `${MAX_WIDTH.single}px`;
		return `${MAX_WIDTH.multiple}px`;
	}};

	min-width: ${({ single, size, isActivity }) => {
		if (isActivity) return "auto";

		if (single) return `${MIN_WIDTH.single}px`;

		if (size.height * 2 >= MAX_WIDTH.multiple) {
			if (size.width < MAX_WIDTH.multiple && size.height < MAX_WIDTH.multiple) {
				return `${MAX_WIDTH.multiple}px`;
			}
			return `${size.width}px`;
		}

		if (size.width < MAX_WIDTH.multiple) {
			return `${MAX_WIDTH.multiple}px`;
		}
		if (size.height < MAX_WIDTH.multiple) {
			return `${MAX_WIDTH.multiple}px`;
		}

		return `${size.width}px`;
	}};
	max-width: ${({ single }) => (single ? "100%" : `${MAX_WIDTH.multiple * 2}px`)};

	img {
		max-width: 100%;
		max-height: ${({ single }) => {
			if (single) return `${MAX_WIDTH.single}px`;
			return `${MAX_WIDTH.multiple}px`;
		}};
	}

	cursor: zoom-in;
	* {
		cursor: zoom-in;
	}

	&:hover {
		.image-icon {
			display: block;
			* {
				cursor: pointer;
			}
		}
	}
`;

const HoverIconArea = styled(Row)`
	display: none;
	position: absolute;
	top: 0;
	right: 0;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	backdrop-filter: blur(10px);
	border-radius: 6px;
	cursor: pointer;
`;
